const drawerSelector = '.header-drawer-shell[data-drawer-id]';
const drawerOpenTriggerSelector = '[data-drawer-open]';
const drawerCloseTriggerSelector = '[data-header-drawer-close]';
const customHeaderDrawerSelector = '.custom-header__drawer.is-open';
const closeTimers = new WeakMap();

if (!window.__headerDrawerShell) {
  const clearCloseTimer = (drawer) => {
    const existingTimer = closeTimers.get(drawer);

    if (existingTimer) {
      window.clearTimeout(existingTimer);
      closeTimers.delete(drawer);
    }
  };

  const getTransitionDurationMs = (element) => {
    if (!(element instanceof HTMLElement)) {
      return 0;
    }

    const computedStyle = window.getComputedStyle(element);
    const parseTimeValue = (value) => {
      const trimmedValue = value.trim();

      if (trimmedValue.endsWith('ms')) {
        return Number.parseFloat(trimmedValue);
      }

      if (trimmedValue.endsWith('s')) {
        return Number.parseFloat(trimmedValue) * 1000;
      }

      return 0;
    };

    const durations = computedStyle.transitionDuration.split(',').map(parseTimeValue);
    const delays = computedStyle.transitionDelay.split(',').map(parseTimeValue);
    const length = Math.max(durations.length, delays.length);
    let longestDuration = 0;

    for (let index = 0; index < length; index += 1) {
      const duration = durations[index] ?? durations[durations.length - 1] ?? 0;
      const delay = delays[index] ?? delays[delays.length - 1] ?? 0;
      longestDuration = Math.max(longestDuration, duration + delay);
    }

    return longestDuration;
  };

  const emitDrawerEvent = (drawer, type) => {
    if (!(drawer instanceof HTMLElement)) {
      return;
    }

    drawer.dispatchEvent(
      new CustomEvent(`header-drawer:${type}`, {
        bubbles: true,
        detail: {
          drawerId: drawer.dataset.drawerId || drawer.id || '',
        },
      })
    );
  };

  const getManagedDrawers = () => Array.from(document.querySelectorAll(drawerSelector));

  const resolveDrawer = (drawerOrId) => {
    if (drawerOrId instanceof HTMLElement) {
      return drawerOrId.matches(drawerSelector) ? drawerOrId : drawerOrId.closest(drawerSelector);
    }

    if (typeof drawerOrId !== 'string' || drawerOrId === '') {
      return null;
    }

    return document.querySelector(`${drawerSelector}[data-drawer-id="${CSS.escape(drawerOrId)}"]`);
  };

  const getDrawerId = (drawer) => {
    if (!(drawer instanceof HTMLElement)) {
      return '';
    }

    return drawer.dataset.drawerId || drawer.id || '';
  };

  const isOpen = (drawerOrId) => {
    const drawer = resolveDrawer(drawerOrId);

    return (
      drawer instanceof HTMLElement &&
      drawer.classList.contains('is-open') &&
      drawer.getAttribute('aria-hidden') === 'false'
    );
  };

  const syncTriggerState = (drawerOrId, forceOpenState) => {
    const drawer = resolveDrawer(drawerOrId);

    if (!(drawer instanceof HTMLElement)) {
      return;
    }

    const drawerId = getDrawerId(drawer);

    if (drawerId === '') {
      return;
    }

    const isDrawerOpen = typeof forceOpenState === 'boolean' ? forceOpenState : isOpen(drawer);

    document.querySelectorAll(drawerOpenTriggerSelector).forEach((trigger) => {
      if (!(trigger instanceof HTMLElement)) {
        return;
      }

      const triggerDrawerId = trigger.dataset.drawerOpen || trigger.getAttribute('aria-controls') || '';

      if (triggerDrawerId !== drawerId) {
        return;
      }

      trigger.setAttribute('aria-expanded', isDrawerOpen ? 'true' : 'false');
    });
  };

  const syncScrollLock = () => {
    const hasOpenManagedDrawer = getManagedDrawers().some((drawer) => isOpen(drawer));
    const hasOpenCustomHeaderDrawer = Boolean(document.querySelector(customHeaderDrawerSelector));

    document.documentElement.toggleAttribute('scroll-lock', hasOpenManagedDrawer || hasOpenCustomHeaderDrawer);
  };

  const finishClose = (drawer) => {
    if (!(drawer instanceof HTMLElement)) {
      return drawer;
    }

    clearCloseTimer(drawer);
    drawer.classList.remove('is-closing');
    drawer.setAttribute('aria-hidden', 'true');
    emitDrawerEvent(drawer, 'close');

    return drawer;
  };

  const close = (drawerOrId) => {
    const drawer = resolveDrawer(drawerOrId);

    if (!(drawer instanceof HTMLElement) || !isOpen(drawer)) {
      return drawer;
    }

    const panel = drawer.querySelector('.header-drawer-shell__panel');
    const transitionDurationMs = getTransitionDurationMs(panel);

    clearCloseTimer(drawer);
    drawer.classList.remove('is-open');
    drawer.classList.add('is-closing');
    syncTriggerState(drawer, false);
    syncScrollLock();

    if (!(panel instanceof HTMLElement) || transitionDurationMs === 0) {
      return finishClose(drawer);
    }

    const handleTransitionEnd = (event) => {
      if (event.target !== panel || event.propertyName !== 'transform') {
        return;
      }

      finishClose(drawer);
    };

    panel.addEventListener('transitionend', handleTransitionEnd, { once: true });

    const closeTimer = window.setTimeout(() => {
      panel.removeEventListener('transitionend', handleTransitionEnd);
      finishClose(drawer);
    }, transitionDurationMs + 50);

    closeTimers.set(drawer, closeTimer);

    return drawer;
  };

  const closeOthers = (activeDrawer) => {
    getManagedDrawers().forEach((drawer) => {
      if (drawer !== activeDrawer) {
        close(drawer);
      }
    });
  };

  const open = (drawerOrId) => {
    const drawer = resolveDrawer(drawerOrId);

    if (!(drawer instanceof HTMLElement)) {
      return null;
    }

    closeOthers(drawer);

    if (isOpen(drawer)) {
      syncTriggerState(drawer, true);
      syncScrollLock();
      return drawer;
    }

    clearCloseTimer(drawer);
    drawer.classList.remove('is-closing');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    syncTriggerState(drawer, true);
    syncScrollLock();
    emitDrawerEvent(drawer, 'open');

    return drawer;
  };

  const toggle = (drawerOrId) => {
    const drawer = resolveDrawer(drawerOrId);

    if (!(drawer instanceof HTMLElement)) {
      return null;
    }

    if (isOpen(drawer)) {
      close(drawer);
      return drawer;
    }

    return open(drawer);
  };

  const getDrawerFromTrigger = (trigger) => {
    if (!(trigger instanceof HTMLElement)) {
      return null;
    }

    const drawerId = trigger.dataset.drawerOpen || trigger.getAttribute('aria-controls') || '';

    return resolveDrawer(drawerId);
  };

  const refresh = () => {
    getManagedDrawers().forEach((drawer) => {
      syncTriggerState(drawer);
    });
    syncScrollLock();
  };

  window.__headerDrawerShell = {
    close,
    getDrawerFromTrigger,
    isOpen,
    open,
    refresh,
    resolveDrawer,
    syncScrollLock,
    syncTriggerState,
    toggle,
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const openTrigger = event.target.closest(drawerOpenTriggerSelector);

    if (openTrigger instanceof HTMLElement) {
      const drawer = getDrawerFromTrigger(openTrigger);

      if (drawer instanceof HTMLElement) {
        toggle(drawer);
      }

      return;
    }

    const closeTrigger = event.target.closest(drawerCloseTriggerSelector);

    if (closeTrigger instanceof HTMLElement) {
      const drawer = closeTrigger.closest(drawerSelector);

      if (drawer instanceof HTMLElement) {
        close(drawer);
      }

      return;
    }

    const mask = event.target.closest(`${drawerSelector} .header-drawer-shell__mask`);

    if (mask instanceof HTMLElement) {
      const drawer = mask.closest(drawerSelector);

      if (drawer instanceof HTMLElement) {
        close(drawer);
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') {
      return;
    }

    const openDrawers = getManagedDrawers().filter((drawer) => isOpen(drawer));
    const drawer = openDrawers[openDrawers.length - 1];

    if (drawer instanceof HTMLElement) {
      close(drawer);
    }
  });

  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('theme:section:rendered', refresh);
  document.addEventListener('shopify:section:unload', syncScrollLock);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, { once: true });
  } else {
    refresh();
  }
}
